/**
 * Pitch Deck Generation Page
 * 
 * Allows users to generate, download, and regenerate their pitch deck.
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { 
  FileText, 
  Download, 
  RefreshCw, 
  Check, 
  AlertCircle, 
  Loader2,
  ArrowLeft,
  FileDown,
  Clock,
  HardDrive,
  Share2
} from "lucide-react";
import { PitchDeckAccessGuard } from "@/components/pitch-deck/PitchDeckAccessGuard";
import { PitchDeckShareDialog } from "@/components/pitch-deck/PitchDeckShareDialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { usePitchDeckGeneration } from "@/hooks/usePitchDeckGeneration";

function GeneratePageContent() {
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const {
    loading,
    generating,
    currentDeck,
    canRegenerate,
    formsComplete,
    handleGenerate,
    handleDownload,
  } = usePitchDeckGeneration();

  // Loading state
  if (loading) {
    return (
      <div className="p-6 md:p-8 max-w-3xl mx-auto">
        <div className="space-y-6">
          <Skeleton className="h-12 w-64" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  // Forms not complete state
  if (!formsComplete) {
    return (
      <div className="p-6 md:p-8 max-w-3xl mx-auto">
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-8 text-center">
            <AlertCircle className="h-16 w-16 text-amber-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-amber-900 mb-2">
              Forms Not Complete
            </h2>
            <p className="text-amber-800 mb-6">
              You need to complete both forms before generating your pitch deck.
            </p>
            <Button asChild>
              <Link to="/pitch-deck/forms">
                Complete Forms →
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 max-w-3xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <Link 
          to="/pitch-deck/forms" 
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Forms
        </Link>
        <h1 className="text-3xl font-bold flex items-center gap-3">
          <FileText className="h-8 w-8 text-primary" />
          Generate Your Pitch Deck
        </h1>
      </div>

      {/* State 1: No deck yet - Ready to Generate */}
      {!currentDeck && !generating && (
        <Card className="shadow-lg">
          <CardContent className="p-8">
            <div className="text-center mb-8">
              <div className="h-20 w-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <FileDown className="h-10 w-10 text-primary" />
              </div>
              <h2 className="text-2xl font-bold mb-2">
                Ready to Create Your Pitch Deck
              </h2>
              <p className="text-muted-foreground">
                Click the button below to generate your professional 15-slide pitch deck.
                This process takes about 30 seconds.
              </p>
            </div>

            <div className="bg-muted rounded-lg p-6 mb-8">
              <h3 className="font-semibold mb-4">What you'll get:</h3>
              <ul className="space-y-3">
                <li className="flex items-center gap-3">
                  <Check className="h-5 w-5 text-green-600" />
                  <span>15 professionally designed slides</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="h-5 w-5 text-green-600" />
                  <span>Your business information formatted beautifully</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="h-5 w-5 text-green-600" />
                  <span>Editable PowerPoint (.pptx) format</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="h-5 w-5 text-green-600" />
                  <span>Ready to present or customize further</span>
                </li>
              </ul>
            </div>

            <Button 
              onClick={() => handleGenerate(false)} 
              size="lg" 
              className="w-full gap-2"
            >
              <FileDown className="h-5 w-5" />
              Generate My Pitch Deck
            </Button>
          </CardContent>
        </Card>
      )}

      {/* State 2: Generating */}
      {generating && (
        <Card className="shadow-lg">
          <CardContent className="p-8">
            <div className="text-center">
              <div className="h-20 w-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
                <Loader2 className="h-10 w-10 text-primary animate-spin" />
              </div>
              <h2 className="text-2xl font-bold mb-2">
                Generating Your Pitch Deck...
              </h2>
              <p className="text-muted-foreground mb-6">
                This may take up to 1 minute. Please don't close this page.
              </p>
              
              {/* Animated progress bar */}
              <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-primary to-primary/60 rounded-full animate-pulse w-3/4" />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* State 3: Success - Deck Generated */}
      {currentDeck && currentDeck.status === "completed" && !generating && (
        <Card className="shadow-lg border-green-200">
          <CardHeader className="bg-green-50 border-b border-green-200">
            <CardTitle className="flex items-center gap-3 text-green-900">
              <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                <Check className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold">Pitch Deck Ready!</h2>
                <p className="text-sm font-normal text-green-700">
                  Your pitch deck has been generated successfully.
                </p>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {/* File Info */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-muted rounded-lg p-4 text-center">
                <HardDrive className="h-5 w-5 text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">File Size</p>
                <p className="font-semibold">
                  {currentDeck.file_size 
                    ? `${Math.round(currentDeck.file_size / 1024)} KB` 
                    : "N/A"}
                </p>
              </div>
              <div className="bg-muted rounded-lg p-4 text-center">
                <Clock className="h-5 w-5 text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">Created</p>
                <p className="font-semibold">
                  {new Date(currentDeck.created_at).toLocaleDateString()}
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button 
                onClick={handleDownload} 
                size="lg" 
                className="flex-1 gap-2"
              >
                <Download className="h-5 w-5" />
                Download
              </Button>
              <Button
                onClick={() => setShareDialogOpen(true)}
                size="lg"
                variant="outline"
                className="gap-2"
              >
                <Share2 className="h-5 w-5" />
                Share
              </Button>
            </div>

            {/* Active public link — so users can see and revoke what's shared */}
            {currentDeck.share_token && (
              <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3">
                <div className="flex items-start gap-2">
                  <Share2 className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-amber-900">
                      This deck has an active public share link
                    </p>
                    <p className="text-xs text-amber-800 mt-0.5">
                      {currentDeck.share_expires_at
                        ? `Expires ${new Date(currentDeck.share_expires_at).toLocaleDateString()}`
                        : "This link never expires"}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShareDialogOpen(true)}
                  >
                    Manage
                  </Button>
                </div>
              </div>
            )}

            {/* Regeneration Option */}
            {canRegenerate && (
              <div className="border-t pt-4 mt-4">
                <p className="text-sm text-muted-foreground text-center mb-3">
                  Not satisfied? You can regenerate your pitch deck once to update the content.
                </p>
                <Button 
                  onClick={() => handleGenerate(true)} 
                  variant="outline" 
                  className="w-full gap-2"
                >
                  <RefreshCw className="h-4 w-4" />
                  Regenerate Pitch Deck (1 time available)
                </Button>
              </div>
            )}

            {!canRegenerate && currentDeck.version > 1 && (
              <p className="text-sm text-muted-foreground text-center mt-4">
                You've used your regeneration. To make changes, edit your forms and generate a new deck.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* State 4: Error */}
      {currentDeck && currentDeck.status === "failed" && !generating && (
        <Card className="shadow-lg border-red-200">
          <CardContent className="p-8">
            <div className="text-center">
              <div className="h-16 w-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="h-8 w-8 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-red-900 mb-2">
                Generation Failed
              </h2>
              <p className="text-red-800 mb-4">
                We couldn't generate your pitch deck.
              </p>
              {currentDeck.error_message && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-6 text-sm text-red-700">
                  {currentDeck.error_message}
                </div>
              )}
            </div>

            <Button 
              onClick={() => handleGenerate(false)} 
              size="lg" 
              className="w-full gap-2"
            >
              <RefreshCw className="h-5 w-5" />
              Try Again
            </Button>

            <p className="text-sm text-muted-foreground text-center mt-4">
              If the problem persists, please contact support.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Still generating state - deck exists but status is generating */}
      {currentDeck && currentDeck.status === "generating" && !generating && (
        <Card className="shadow-lg">
          <CardContent className="p-8">
            <div className="text-center">
              <div className="h-20 w-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
                <Loader2 className="h-10 w-10 text-primary animate-spin" />
              </div>
              <h2 className="text-2xl font-bold mb-2">
                Generation In Progress
              </h2>
              <p className="text-muted-foreground mb-6">
                Your pitch deck is being generated. This page will update automatically.
              </p>
              <Button variant="outline" onClick={() => window.location.reload()}>
                Refresh Status
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Share Dialog */}
      {currentDeck && (
        <PitchDeckShareDialog
          pitchDeckId={currentDeck.id}
          shareToken={currentDeck.share_token}
          shareExpiresAt={currentDeck.share_expires_at}
          open={shareDialogOpen}
          onOpenChange={setShareDialogOpen}
        />
      )}
    </div>
  );
}

export default function PitchDeckGenerate() {
  return (
    <PitchDeckAccessGuard>
      <GeneratePageContent />
    </PitchDeckAccessGuard>
  );
}
